import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  Link,
  IconButton,
  InputLabel,
  OutlinedInput,
  Stack,
  SvgIcon,
  Typography,
  TextField,
  FormControl,
  InputAdornment,
  Divider,
  useMediaQuery,
  Drawer,
} from '@mui/material';
import { MuiOtpInput } from 'mui-one-time-password-input';
import PhoneInput from 'components/country-code-picker';
import { updateContact } from 'api/users';
import OneTimePassword from '../login/OneTimePassword';
import { useSnackbar } from 'components/snackbar';
import { useAuth } from 'hooks/useAuthContext';
import { useTheme } from '@mui/material/styles';
import { useBackNavigationDialog } from 'hooks/useBackNavigationDialog'; // NOTE: Adjust the import path as needed


/**
 * UpdateWhatsapp component allows users to update their WhatsApp phone number.
 * It verifies the phone number with an OTP sent to the provided number.
 * 
 * Props:
 * - onCancel: Function to handle cancellation of the update process.
 * - onCancelWithNumber: Function to handle cancellation with the provided phone number.
 * - isResume: Boolean to indicate if the resume needs to be updated.
 * - handleResumeProfile: Function to handle resume profile update.
 * 
 * State:
 * - showFlag: Boolean to indicate if the country code flag should be displayed.
 * - phoneNumber: State to store the input phone number.
 * - showOtpInput: Boolean to control the display of OTP input.
 * - open: Boolean to control the OTP dialog visibility.
 * - userId: State to store the user ID for OTP verification.
 * - error: State to store error messages for invalid input.
 * - isRemoving: Boolean to control the loading state of the verify button.
 * 
 * Functions:
 * - handleRemove: Triggers the phone number submission and OTP process.
 * - handleOpenOtpDialog: Opens the OTP dialog.
 * - handleClose: Closes the OTP dialog and triggers onCancelWithNumber if successful.
 * - handleFlag: Displays the country code flag.
 * - handleChange: Validates and updates the phone number input.
 * - handleSubmit: Submits the phone number for verification and handles OTP sending.
 * - handleOnBlurFlag: Hides the flag if no phone number is entered.
 */

const UpdateWhatsapp = ({ onCancel, onCancelWithNumber, isResume, handleResumeProfile }) => {
  const { user, updateContext } = useAuth();
  const { openDialog, closeDialog, dialogLocation } = useBackNavigationDialog();
  
  const dialogIds = dialogLocation.state?.dialogIds || [];

  const open = dialogIds.includes('updateWhatsappOtpDialog');

  const [showFlag, setShowFlag] = React.useState(user?.phone ? true : false);
  const [phoneNumber, setPhoneNumber] = React.useState(user?.phone || '');
  const [showOtpInput, setShowOtpInput] = React.useState(false);
  // const [open, setOpen] = React.useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [userId, setUserId] = React.useState(null);
  const [error, setError] = React.useState('');
  const [isRemoving, setIsRemoving] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  /**
   * Initiates the phone number submission and OTP process by setting
   * the removing state to true and calling the handleSubmit function.
   */

  const handleRemove = () => {
    setIsRemoving(true);
    handleSubmit();
  };

  /**
   * Opens the OTP dialog.
   */
  const handleOpenOtpDialog = () => {
    openDialog('updateWhatsappOtpDialog');
  };
  /**
   * Closes the OTP dialog and calls onCancelWithNumber with the current phone number
   * if the message is 'successfully'. Sets the open state to false to close the dialog.
   * 
   * @param {string} message - The message indicating the action to be taken.
   */
  const handleClose = (message) => {
    if (message === 'successfully') {
      onCancelWithNumber(phoneNumber);
    }
    closeDialog('updateWhatsappOtpDialog');
  };

  /**
   * Sets the showFlag state to true, indicating that a certain
   * condition or flag should be displayed or activated.
   */

  const handleFlag = () => {
    setShowFlag(true);
  };

  /**
   * Validates and updates the phone number input.
   * If the phone number is 10 digits long, sets showFlag to true and error to an empty string.
   * If the phone number is invalid, sets showFlag to false and error to an appropriate error message.
   * Updates the state of phoneNumber with the input value.
   * @param {event} e - The input event containing the phone number value.
   */
  const handleChange = (e) => {
    const value = e.target.value;
    if (value.length === 10) {
      setShowFlag(true);
      setError('');
    } else {
      setError('Phone number must be 10 digits long. Please enter a valid number.');
    }
    setPhoneNumber(value);
  };

  /**
   * Submits the phone number input and initiates the OTP verification process.
   * If the phone number input is invalid, sets the error state to an appropriate
   * error message and returns without calling the API.
   * If the phone number is valid, calls the updateContact API with the phone number.
   * If the API call is successful, sets the userId state and opens the OTP dialog.
   * If the API call is unsuccessful, sets the error state to the API response message.
   * Sets the isRemoving state to false after the API call or if the input is invalid.
   */
  const handleSubmit = async () => {
    if (phoneNumber.length !== 10) {
      setError('Phone number must be 10 digits long. Please enter a valid number.');
      setIsRemoving(false);
      return;
    }
    if (!open) {
      if (user?.phone === phoneNumber && user?.is_phone_verified) {
        enqueueSnackbar("This phone number is already verified.", { variant: 'error' });
        setIsRemoving(false)
        return
      }
      const response = await updateContact({ phoneNumber });
      if (response?.success === true && response.data?.id) {
        setUserId(response.data.id);
        enqueueSnackbar(`OTP has been sent.`, { variant: 'success' });
        handleOpenOtpDialog();
      } else {
        enqueueSnackbar(response.message, { variant: 'error' });
      }
    }
    setIsRemoving(false);
  };

  /**
   * Toggles the visibility of the flag icon based on the phone number input field's focus.
   * If the phone number is empty, the flag icon is hidden.
   */

  const handleOnBlurFlag = () => {
    if (!phoneNumber) {
      setShowFlag(false);
    }
  }

  return (
    <Box>
      <Card className="otp-card-wrapper" variant="outlined">
        <CardContent>
          {user?.phone &&
            <IconButton sx={{ display: { xs: "none", sm: "none" } }} className="close-dialog-btn" aria-label="delete" color="primary" onClick={onCancel}>
              <SvgIcon sx={{ width: '20px', height: '20px' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <g clipPath="url(#clip0_456_20993)">
                    <path
                      d="M7 7L17 17M17 7L7 17L17 7Z"
                      stroke="#111827"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_456_20993">
                      <rect width="24" height="24" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </SvgIcon>
            </IconButton>
          }

          <Box mb={2.5} sx={{ textAlign: 'left' }}>
            <Typography mb={1} className="title" component="h6">
              Update WhatsApp Number
            </Typography>
            <Typography sx={{ textAlign: 'left!important' }} className="otp-sent-txt" component="p">
              This number would be used for primary communication by your recruiters
            </Typography>
          </Box>

          <Box mb={4} >
            <FormControl
              fullWidth
              className="wh-number-input"
              size="small"
              onClick={handleFlag}
              onChange={handleChange}
              onBlur={handleOnBlurFlag}
              onFocus={handleFlag}
              sx={{
                display: 'inline-flex',
                flexDirection: 'column',
                position: 'relative',
                minWidth: '0px',
                padding: '0px',
                margin: '0px',
                border: '0px',
                verticalAlign: 'top',
                width: '100%',
                height: '60px',
              }}
            >
              {showFlag && (
                <Box className="iti-flag-container" rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 2 }} mb={3}>
                  <Stack direction="row" alignItems={'center'} spacing={1}>
                    <Box className="iti-flag"></Box>
                    <Box class="selected-dial-code">+91</Box>
                    <Divider orientation="vertical" variant="middle" flexItem />
                  </Stack>
                </Box>
              )}
              <TextField
                size="small"
                id="phone_number"
                name="phone_number"
                label="WhatsApp Number"
                type="number"
                // {...register('phone_number')}
                // error={!!errors.phone_number}
                // helperText={errors.phone_number?.message}
                onChange={handleChange}
                value={phoneNumber}
                error={!!error}
                helperText={error}
                InputLabelProps={{
                  shrink: !!phoneNumber || showFlag,
                }}
              />
              {/* {error && (
                <Typography color="error" variant="body2">
                  {error}
                </Typography>
              )} */}

            </FormControl>
          </Box>

          <Stack direction="row" alignItems={'center'} spacing={3}>
            <Button fullWidth className="popup-btn" variant="outlined" size="medium" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              fullWidth
              className="popup-btn"
              variant="contained"
              size="medium"
              onClick={handleRemove}
              disabled={isRemoving}
            >
              {isRemoving ? 'Sending...' : 'Verify'}
            </Button>
          </Stack>
        </CardContent>
      </Card>
      {isMobile ?
        <Drawer
          anchor="bottom"
          open={open}
          onClose={(event, reason) => {
            if (reason !== 'backdropClick') {
              closeDialog('updateWhatsappOtpDialog')
            }
          }}
          className="forgot-password-dialog"
        >
          <OneTimePassword userId={userId} onCancelOTP={handleClose} phoneNumber={phoneNumber} isRegister={false} isEmailVerified={false} isPhoneVerified={true} isResume={isResume} handleResumeProfile={handleResumeProfile} />
        </Drawer>
        :
        <Dialog
          open={open}
          onClose={() => closeDialog('updateWhatsappOtpDialog')}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          className="otp-dialog-wrapper"
        >
          <OneTimePassword userId={userId} onCancelOTP={handleClose} phoneNumber={phoneNumber} isRegister={false} isEmailVerified={false} isPhoneVerified={true} isResume={isResume} handleResumeProfile={handleResumeProfile} />
        </Dialog>
      }
    </Box>
  );
};

export default UpdateWhatsapp;
